import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { slugify } from '../lib/slug';
import { formatMoney } from '../lib/format';

type View = 'panel' | 'restaurants' | 'award' | 'finance' | 'leads';
interface DemoLead { id: string; name: string; email: string | null; phone: string | null; org_name: string | null; city: string | null; message: string | null; created_at: string }
interface PartnerLead { id: string; org_name: string; contact_name: string | null; email: string | null; phone: string | null; message: string | null; created_at: string }

interface Row {
  id: string; name: string; slug: string; manual_billing: boolean; created_at: string;
  country: string | null; state: string | null; city: string | null; currency: string;
}

const PRO_BRL = 199;
const IA_BRL = 49;
const CURRENCIES = ['BRL', 'USD', 'EUR'];
const LOCALES = ['pt-BR', 'en', 'es', 'fr'];
const TIMEZONES = ['America/Sao_Paulo', 'America/New_York', 'Europe/Lisbon', 'Europe/Paris', 'Europe/Madrid'];

export function MasterPage({ view = 'panel' }: { view?: View }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [subs, setSubs] = useState<Record<string, { status: string; ai: boolean }>>({});
  const [nps, setNps] = useState<Record<string, { sum: number; n: number }>>({});
  const [rev, setRev] = useState<Record<string, number>>({});
  const [tables, setTables] = useState<Record<string, number>>({});
  const [customers, setCustomers] = useState(0);

  // criar (cortesia)
  const [name, setName] = useState('');
  const [locale, setLocale] = useState('pt-BR');
  const [currency, setCurrency] = useState('BRL');
  const [timezone, setTimezone] = useState('America/Sao_Paulo');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // filtros (restaurantes)
  const [fCountry, setFCountry] = useState('');
  const [fState, setFState] = useState('');
  const [fCity, setFCity] = useState('');

  // dono (atribuir / resetar)
  const [demoLeads, setDemoLeads] = useState<DemoLead[]>([]);
  const [partnerLeads, setPartnerLeads] = useState<PartnerLead[]>([]);
  const [ownerByRid, setOwnerByRid] = useState<Record<string, string>>({});
  const [ownerOpen, setOwnerOpen] = useState<string | null>(null);
  const [oEmail, setOEmail] = useState('');
  const [oPass, setOPass] = useState('');
  const [oName, setOName] = useState('');
  const [oMsg, setOMsg] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const { data } = await supabase.from('restaurants')
      .select('id, name, slug, manual_billing, created_at, country, state, city, currency')
      .order('created_at', { ascending: false });
    if (data) setRows(data as Row[]);

    const { data: s } = await supabase.from('subscriptions').select('restaurant_id, status, ai_addon');
    if (s) setSubs(Object.fromEntries((s as Array<{ restaurant_id: string; status: string; ai_addon: boolean }>).map((x) => [x.restaurant_id, { status: x.status, ai: x.ai_addon }])));

    const { data: f } = await supabase.from('feedback').select('restaurant_id, score');
    const nm: Record<string, { sum: number; n: number }> = {};
    (f as Array<{ restaurant_id: string; score: number }> | null)?.forEach((x) => {
      nm[x.restaurant_id] = nm[x.restaurant_id] ?? { sum: 0, n: 0 };
      nm[x.restaurant_id].sum += x.score; nm[x.restaurant_id].n += 1;
    });
    setNps(nm);

    const { data: a } = await supabase.from('analytics_events').select('restaurant_id, total_paid_cents');
    const rv: Record<string, number> = {};
    (a as Array<{ restaurant_id: string; total_paid_cents: number }> | null)?.forEach((x) => { rv[x.restaurant_id] = (rv[x.restaurant_id] ?? 0) + (x.total_paid_cents ?? 0); });
    setRev(rv);

    const { data: tb } = await supabase.from('tables').select('restaurant_id');
    const tc: Record<string, number> = {};
    (tb as Array<{ restaurant_id: string }> | null)?.forEach((x) => { tc[x.restaurant_id] = (tc[x.restaurant_id] ?? 0) + 1; });
    setTables(tc);

    const { count } = await supabase.from('loyalty_accounts').select('id', { count: 'exact', head: true });
    setCustomers(count ?? 0);

    const { data: dl } = await supabase.from('demo_leads').select('id, name, email, phone, org_name, city, message, created_at').order('created_at', { ascending: false });
    if (dl) setDemoLeads(dl as DemoLead[]);
    const { data: pl } = await supabase.from('partner_leads').select('id, org_name, contact_name, email, phone, message, created_at').order('created_at', { ascending: false });
    if (pl) setPartnerLeads(pl as PartnerLead[]);

    const { data: mem } = await supabase.from('restaurant_members').select('restaurant_id, profiles(full_name)').eq('role', 'owner');
    const om: Record<string, string> = {};
    (mem as Array<{ restaurant_id: string; profiles: { full_name: string | null } | null }> | null)?.forEach((x) => { om[x.restaurant_id] = x.profiles?.full_name || '—'; });
    setOwnerByRid(om);
  }, []);

  async function assignOwner(rid: string) {
    setOMsg(null);
    const { data, error: err } = await supabase.functions.invoke('master-users', { body: { action: 'create_owner', restaurant_id: rid, email: oEmail, password: oPass, name: oName } });
    const d = data as { ok?: boolean; error?: string } | null;
    if (err || !d?.ok) return setOMsg(d?.error ?? t('master.owner_fail'));
    setOEmail(''); setOPass(''); setOName(''); setOwnerOpen(null); void reload();
  }
  async function resetOwner(rid: string) {
    setOMsg(null);
    const { data, error: err } = await supabase.functions.invoke('master-users', { body: { action: 'reset_password', restaurant_id: rid, password: oPass } });
    const d = data as { ok?: boolean; error?: string } | null;
    if (err || !d?.ok) return setOMsg(d?.error ?? t('master.owner_fail'));
    setOPass(''); setOMsg(t('master.owner_ok'));
  }

  useEffect(() => { void reload(); }, [reload]);

  async function create(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true); setError(null);
    const { error: err } = await supabase.rpc('master_create_restaurant', {
      p_name: name, p_slug: slugify(name) + '-' + Math.random().toString(36).slice(2, 6), p_locale: locale, p_currency: currency, p_timezone: timezone
    });
    setBusy(false);
    if (err) return setError(err.message);
    setName(''); void reload();
  }
  async function toggle(r: Row) {
    await supabase.rpc('master_set_manual_billing', { p_rid: r.id, p_on: !r.manual_billing });
    void reload();
  }

  // métricas
  const m = useMemo(() => {
    let active = 0, trial = 0, pastDue = 0, canceled = 0, courtesy = 0, mrr = 0;
    for (const r of rows) {
      const st = subs[r.id]?.status;
      if (r.manual_billing) courtesy += 1;
      else if (st === 'active') { active += 1; mrr += PRO_BRL + (subs[r.id]?.ai ? IA_BRL : 0); }
      else if (st === 'trialing') trial += 1;
      else if (st === 'past_due') pastDue += 1;
      else if (st === 'canceled') canceled += 1;
    }
    const churn = active + canceled > 0 ? Math.round((canceled / (active + canceled)) * 100) : 0;
    return { active, trial, pastDue, canceled, courtesy, mrr, churn };
  }, [rows, subs]);

  const countries = useMemo(() => [...new Set(rows.map((r) => r.country).filter(Boolean))] as string[], [rows]);
  const states = useMemo(() => [...new Set(rows.filter((r) => !fCountry || r.country === fCountry).map((r) => r.state).filter(Boolean))] as string[], [rows, fCountry]);
  const cities = useMemo(() => [...new Set(rows.filter((r) => (!fCountry || r.country === fCountry) && (!fState || r.state === fState)).map((r) => r.city).filter(Boolean))] as string[], [rows, fCountry, fState]);
  const filtered = rows.filter((r) => (!fCountry || r.country === fCountry) && (!fState || r.state === fState) && (!fCity || r.city === fCity));

  const subtitles: Record<View, string> = { panel: 'master.subtitle', restaurants: 'master.sub_restaurants', award: 'master.sub_award', finance: 'master.sub_finance', leads: 'master.sub_leads' };

  return (
    <div className="page page--wide">
      <h1 className="page__title">{t('master.title')}</h1>
      <p className="page__subtitle">{t(subtitles[view])}</p>

      {/* PAINEL */}
      {view === 'panel' && (
        <div className="stat-grid">
          <div className="stat"><span className="stat__label">{t('master.m_restaurants')}</span><span className="stat__value">{rows.length}</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_active')}</span><span className="stat__value">{m.active}</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_trial')}</span><span className="stat__value">{m.trial}</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_courtesy')}</span><span className="stat__value">{m.courtesy}</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_customers')}</span><span className="stat__value">{customers}</span></div>
          <div className="stat"><span className="stat__label">{t('master.mrr')}</span><span className="stat__value">{formatMoney(m.mrr * 100, 'BRL', 'pt-BR')}</span></div>
        </div>
      )}

      {/* RESTAURANTES */}
      {view === 'restaurants' && (
        <>
          <h2 className="section-title">{t('master.create_title')}</h2>
          <form onSubmit={create} className="form">
            <label className="field"><span>{t('master.name')}</span><input required value={name} onChange={(e) => setName(e.target.value)} /></label>
            <div className="form-row">
              <label className="field"><span>{t('master.locale')}</span><select value={locale} onChange={(e) => setLocale(e.target.value)}>{LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}</select></label>
              <label className="field"><span>{t('master.currency')}</span><select value={currency} onChange={(e) => setCurrency(e.target.value)}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
              <label className="field"><span>{t('master.timezone')}</span><select value={timezone} onChange={(e) => setTimezone(e.target.value)}>{TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}</select></label>
            </div>
            {error && <p className="form-error">{error}</p>}
            <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? t('master.creating') : t('master.create_cta')}</button>
            <span className="muted-note">{t('master.create_hint')}</span>
          </form>

          <h2 className="section-title">{t('master.all')} <span className="tag">{filtered.length}</span></h2>
          <div className="form-row">
            <select value={fCountry} onChange={(e) => { setFCountry(e.target.value); setFState(''); setFCity(''); }}><option value="">{t('master.f_country')}</option>{countries.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            <select value={fState} onChange={(e) => { setFState(e.target.value); setFCity(''); }}><option value="">{t('master.f_state')}</option>{states.map((s) => <option key={s} value={s}>{s}</option>)}</select>
            <select value={fCity} onChange={(e) => setFCity(e.target.value)}><option value="">{t('master.f_city')}</option>{cities.map((c) => <option key={c} value={c}>{c}</option>)}</select>
          </div>
          <ul className="list">
            {filtered.map((r) => (
              <li key={r.id} className="master-rest">
                <div className="master-rest__head">
                  <span>
                    <strong>{r.name}</strong> <span className="dimmed">{[r.city, r.state].filter(Boolean).join(', ')}</span>
                    {r.manual_billing
                      ? <em className="tag" style={{ marginLeft: 6 }}>{t('master.manual_on')}</em>
                      : <em className="tag" style={{ marginLeft: 6 }}>{t('master.st_' + (subs[r.id]?.status ?? 'none'))}</em>}
                    <span className="dimmed" style={{ marginLeft: 6 }}>· {t('master.owner_label')}: {ownerByRid[r.id] ?? t('master.owner_none')}</span>
                  </span>
                  <span className="row-actions">
                    <button type="button" className="link-button" onClick={() => { setOwnerOpen(ownerOpen === r.id ? null : r.id); setOMsg(null); }}>{t('master.owner_manage')}</button>
                    <button type="button" className="link-button" onClick={() => void toggle(r)}>{r.manual_billing ? t('master.turn_off') : t('master.turn_on')}</button>
                  </span>
                </div>
                {ownerOpen === r.id && (
                  <div className="add-panel">
                    {ownerByRid[r.id] ? (
                      <div className="add-panel__row">
                        <input className="onb-input" type="password" placeholder={t('master.owner_newpass')} value={oPass} onChange={(e) => setOPass(e.target.value)} />
                        <button type="button" className="btn btn--ghost btn--sm" disabled={oPass.length < 6} onClick={() => void resetOwner(r.id)}>{t('master.owner_reset')}</button>
                      </div>
                    ) : (
                      <>
                        <div className="add-panel__row">
                          <input className="onb-input" placeholder={t('master.owner_email')} value={oEmail} onChange={(e) => setOEmail(e.target.value)} />
                          <input className="onb-input" placeholder={t('master.owner_name')} value={oName} onChange={(e) => setOName(e.target.value)} />
                        </div>
                        <div className="add-panel__row">
                          <input className="onb-input" type="password" placeholder={t('master.owner_pass')} value={oPass} onChange={(e) => setOPass(e.target.value)} />
                          <button type="button" className="btn btn--primary btn--sm" disabled={!oEmail || oPass.length < 6} onClick={() => void assignOwner(r.id)}>{t('master.owner_assign')}</button>
                        </div>
                      </>
                    )}
                    {oMsg && <span className="muted-note">{oMsg}</span>}
                  </div>
                )}
              </li>
            ))}
            {filtered.length === 0 && <li className="list__row muted-note">{t('master.none')}</li>}
          </ul>
        </>
      )}

      {/* PRÊMIO (NPS + receita/mesa) */}
      {view === 'award' && (
        <ul className="list">
          <li className="list__row" style={{ fontWeight: 600, color: 'var(--color-muted)' }}>
            <span>{t('master.col_rest')}</span>
            <span className="row-actions"><span style={{ width: 90, textAlign: 'right' }}>{t('master.col_nps')}</span><span style={{ width: 120, textAlign: 'right' }}>{t('master.col_rev_table')}</span></span>
          </li>
          {rows.map((r) => {
            const np = nps[r.id]; const avg = np && np.n ? (np.sum / np.n) : null;
            const tcount = tables[r.id] ?? 0; const perTable = tcount ? Math.round((rev[r.id] ?? 0) / tcount) : null;
            return (
              <li key={r.id} className="list__row">
                <span><strong>{r.name}</strong> <span className="dimmed">{r.city ?? ''}</span></span>
                <span className="row-actions">
                  <span style={{ width: 90, textAlign: 'right' }}>{avg != null ? avg.toFixed(1) : '—'}</span>
                  <span style={{ width: 120, textAlign: 'right' }}>{perTable != null ? formatMoney(perTable, r.currency, 'pt-BR') : '—'}</span>
                </span>
              </li>
            );
          })}
          {rows.length === 0 && <li className="list__row muted-note">{t('master.none')}</li>}
        </ul>
      )}

      {/* FINANCEIRO */}
      {view === 'finance' && (
        <div className="stat-grid">
          <div className="stat"><span className="stat__label">{t('master.mrr')}</span><span className="stat__value">{formatMoney(m.mrr * 100, 'BRL', 'pt-BR')}</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_active')}</span><span className="stat__value">{m.active}</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_trial')}</span><span className="stat__value">{m.trial}</span></div>
          <div className="stat"><span className="stat__label">{t('master.st_past_due')}</span><span className="stat__value">{m.pastDue}</span></div>
          <div className="stat"><span className="stat__label">{t('master.st_canceled')}</span><span className="stat__value">{m.canceled}</span></div>
          <div className="stat"><span className="stat__label">{t('master.churn')}</span><span className="stat__value">{m.churn}%</span></div>
          <div className="stat"><span className="stat__label">{t('master.m_courtesy')}</span><span className="stat__value">{m.courtesy}</span></div>
        </div>
      )}

      {view === 'leads' && (
        <>
          <h2 className="section-title">{t('master.leads_demo')} <span className="tag">{demoLeads.length}</span></h2>
          <ul className="list">
            {demoLeads.map((l) => (
              <li key={l.id} className="list__row">
                <span>
                  <strong>{l.name}</strong> <span className="dimmed">{[l.org_name, l.city].filter(Boolean).join(' · ')}</span><br />
                  <span className="dimmed">{[l.email, l.phone].filter(Boolean).join(' · ')}{l.message ? ` — ${l.message}` : ''}</span>
                </span>
                <span className="dimmed">{new Date(l.created_at).toLocaleDateString()}</span>
              </li>
            ))}
            {demoLeads.length === 0 && <li className="list__row muted-note">—</li>}
          </ul>

          <h2 className="section-title">{t('master.leads_partners')} <span className="tag">{partnerLeads.length}</span></h2>
          <ul className="list">
            {partnerLeads.map((l) => (
              <li key={l.id} className="list__row">
                <span>
                  <strong>{l.org_name}</strong> <span className="dimmed">{l.contact_name}</span><br />
                  <span className="dimmed">{[l.email, l.phone].filter(Boolean).join(' · ')}{l.message ? ` — ${l.message}` : ''}</span>
                </span>
                <span className="dimmed">{new Date(l.created_at).toLocaleDateString()}</span>
              </li>
            ))}
            {partnerLeads.length === 0 && <li className="list__row muted-note">—</li>}
          </ul>
        </>
      )}
    </div>
  );
}
